// TanStack Query defaults per app (Frontend §3.3).
import { QueryClient, type QueryClientConfig } from '@tanstack/react-query'
import { isApiError } from './errors'

export type QueryAudience = 'patient' | 'staff'

/** Patient queries retry these once (threat analysis §5: "browser retry once, then show the error"). */
const PATIENT_RETRYABLE = new Set(['network_error', 'timeout', 'internal_error'])
/** A patient 429 is retried once only when the server asks for at most this long. */
const PATIENT_MAX_RETRY_AFTER_SEC = 5

/** Patient: 1 retry for network_error, timeout and internal_error; a 429 only with Retry-After ≤ 5 s. Never 4xx or pf_*. */
export function patientShouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1 || !isApiError(error)) return false
  if (error.code === 'rate_limited') {
    return error.retryAfterSec !== undefined && error.retryAfterSec <= PATIENT_MAX_RETRY_AFTER_SEC
  }
  return PATIENT_RETRYABLE.has(error.code)
}

/** Staff: 2 retries for network_error, timeout and 5xx. Never 4xx. */
export function staffShouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2 || !isApiError(error)) return false
  return error.code === 'network_error' || error.code === 'timeout' || error.status >= 500
}

/** Patient: jittered 300–800 ms; a 429 waits for its Retry-After. */
export function patientRetryDelay(_failureCount: number, error: unknown): number {
  if (isApiError(error) && error.code === 'rate_limited' && error.retryAfterSec !== undefined) {
    return error.retryAfterSec * 1000
  }
  return 300 + Math.round(Math.random() * 500)
}

/** Staff: exponential (1 s, 2 s, … capped at 8 s) with up to 30% jitter. */
export function staffRetryDelay(failureCount: number): number {
  const base = Math.min(1000 * 2 ** failureCount, 8000)
  return Math.round(base * (1 + Math.random() * 0.3))
}

export interface CreateQueryClientOptions {
  /** Tests: the app's real retry policy, but no wait between attempts (Testing §3 "Rendering"). */
  noRetryDelay?: boolean
}

/** The app's QueryClient with the §3.3 defaults. `staleTime` and polling are set per resource by each feature. */
export function createQueryClient(audience: QueryAudience, options: CreateQueryClientOptions = {}): QueryClient {
  const patient = audience === 'patient'
  const retryDelay = options.noRetryDelay ? 0 : patient ? patientRetryDelay : staffRetryDelay
  const config: QueryClientConfig = {
    defaultOptions: {
      queries: {
        retry: patient ? patientShouldRetry : staffShouldRetry,
        retryDelay,
        // Patient: off, except availability (set by that query). Staff: on for lists and health.
        refetchOnWindowFocus: !patient,
        // Screens map errors to states (patient) or show an inline error with Retry (staff).
        throwOnError: false,
      },
      mutations: {
        // Holds and bookings are never retried by Query (§4.10 has the explicit booking replay); nor is any
        // staff mutation.
        retry: false,
        throwOnError: false,
      },
    },
  }
  return new QueryClient(config)
}
